import { File } from '@awesome-cordova-plugins/file/ngx';
import { UiHelper } from 'src/app/api/helper/ui-helper';
import { Utils } from 'src/app/api/helper/utils';
import { FileHelper } from 'src/app/api/helper/file-helper';
import { Logger } from 'src/app/api/helper/app-error-logger';
import { MyApp } from 'src/app/api/helper/app';
import { AppConfig } from 'src/app/api/helper/app.config';
import { SystemService } from 'src/app/api/system.service';
import { ImageHelper } from 'src/app/api/helper/image-helper';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';

import moment from 'moment';
import { from } from 'rxjs';

const log = new Logger('CameraHelper');

export interface PictureOptions {
    filenamePrefix: string;
    text?: string;
    textPlatzhalter?: any;
    fromLibrary?: boolean;
}

export class CameraHelper {
    static isAktiv = false;

    static file: File;

    static async pickPicturesFromLibrary(options: PictureOptions): Promise<string[]> {
        try {
            const result = await Camera.pickImages({
                quality: 95,
                limit: 50,
            });

            log?.debug('pickImages result: ' + JSON.stringify(result));

            if (result?.photos?.length) {
                const imageUrls = result.photos.map(photo => photo.path);

                for (let i = 0; i < imageUrls.length; i++) {
                    let pictureSrc = imageUrls[i];

                    if (pictureSrc && pictureSrc.startsWith('file:')) {
                        // Beispiel pictureSrc: file:///data/user/0/de.recomobil.app/cache/1000011627.1753096516156.jpeg
                        log?.debug('pictureSrc: ' + pictureSrc);

                        // Merke den alten Pfad
                        const oldPictureSrc = pictureSrc;

                        // Extract just the filename. Result example: cdv_photo_003.jpg
                        const tempFilename = FileHelper.getFilename(pictureSrc);

                        log?.debug('tempFilename: ' + tempFilename);

                        // Now, the opposite. Extract the full path, minus filename.
                        // Result example: file:///var/mobile/Containers/Data/Application
                        // /E4A79B4A-E5CB-4E0C-A7D9-0603ECD48690/tmp/
                        const tempBaseFilesystemPath = FileHelper.getDirectory(pictureSrc);

                        log?.debug('tempBaseFilesystemPath: ' + tempBaseFilesystemPath);

                        // Get the Data directory on the device.
                        // Result example: file:///var/mobile/Containers/Data/Application
                        // /E4A79B4A-E5CB-4E0C-A7D9-0603ECD48690/Library/NoCloud/
                        const newBaseFilesystemPath = this.file.dataDirectory;
                        const newFilename = FileHelper.removeIllegalFilenameChars(options.filenamePrefix + '-' + tempFilename);

                        log?.debug('newBaseFilesystemPath: ' + newBaseFilesystemPath);
                        log?.debug('newFilename: ' + newFilename);

                        const entry = await this.file.copyFile(tempBaseFilesystemPath, tempFilename, newBaseFilesystemPath, newFilename);

                        log?.debug('copyFile: ' + JSON.stringify(entry));

                        // Result example: file:///var/mobile/Containers/Data/Application
                        // /E4A79B4A-E5CB-4E0C-A7D9-0603ECD48690/Library/NoCloud/cdv_photo_003.jpg
                        pictureSrc = newBaseFilesystemPath + newFilename;

                        imageUrls[i] = pictureSrc;

                        let text = options.text;

                        if (text == null) {
                            text = AppConfig.current.einstellungen.FotosTextSonstige;
                        }

                        if (text) {
                            log?.debug('Adding text to image: ' + text);

                            const now = moment();

                            let platzhalter: any = {
                                'AktuellesDatum': now.format('DD.MM.YYYY'),
                                'AktuelleUhrzeit': now.format('HH:mm'),
                                'Fahrzeug': MyApp.current.getFahrzeugKennzeichen(),
                                'PersonalKey': MyApp.current.getPersonalKey(),
                                'Personalnummer': MyApp.current.getPersonalnummer(),
                                'PersonalName': MyApp.current.getPersonalName(),
                            }

                            if (text.includes('{GPS}')) {
                                platzhalter['GPS'] = await SystemService.instance.getAktuelleGpsPositionStr('Foto', 10);
                            }

                            if (options.textPlatzhalter) {
                                platzhalter = {
                                    ...platzhalter,
                                    ...options.textPlatzhalter
                                };
                            }

                            const base64DataUrl = await this.file.readAsDataURL(newBaseFilesystemPath, newFilename);

                            const blob = await ImageHelper.writeImageText(base64DataUrl, text, platzhalter);

                            log?.debug('blob: ', blob);

                            await this.file.writeExistingFile(newBaseFilesystemPath, newFilename, blob);
                        }
                    }
                }

                return imageUrls;
            }
        } catch (err) {
            log.error('Fehler beim Bildauswahl: ' + Utils.getErrorMessage(err), err);
        }

        return [];
    }

    static async takePicture(options: PictureOptions): Promise<string> {
        let pictureSrc: string;

        CameraHelper.isAktiv = true;

        try {
            const permissions = await Camera.checkPermissions();

            log?.debug('Camera permissions: ' + JSON.stringify(permissions));

            const permissions2 = await Camera.requestPermissions({
                permissions: options.fromLibrary
                    ? ['camera', 'photos']
                    : ['camera']
            });

            log?.debug('Camera permissions 2: ' + JSON.stringify(permissions2));

            const cameraOptions = {
                quality: MyApp.current.qualitaetFotos,
                allowEditing: false,
                resultType: CameraResultType.Uri,
                width: MyApp.current.aufloesungFotos,
                height: MyApp.current.aufloesungFotos,
                correctOrientation: true,
                saveToGallery: false,
                source: CameraSource.Camera
                // promptLabelHeader: 'Foto aufnehmen',
                // promptLabelPhoto: 'Foto aufnehmen',
            };

            if (options.fromLibrary) {
                cameraOptions.source = CameraSource.Photos;
            }

            const image = await Camera.getPhoto(cameraOptions);

            log?.debug('image: ' + JSON.stringify(image), image);

            // image.webPath will contain a path that can be set as an image src.
            // You can access the original file using image.path, which can be
            // passed to the Filesystem API to read the raw data of the image,
            // if desired (or pass resultType: CameraResultType.Base64 to getPhoto)
            pictureSrc = image.path;

            // Can be set to the src of an image now
            // imageElement.src = imageUrl;



            // pictureSrc = await CameraHelper.camera.getPicture({
            //     destinationType: this.camera.DestinationType.FILE_URI,
            //     encodingType: this.camera.EncodingType.JPEG,
            //     mediaType: this.camera.MediaType.PICTURE,
            //     sourceType: this.camera.PictureSourceType.CAMERA,
            //     quality: MyApp.current.qualitaetFotos,
            //     targetWidth: MyApp.current.aufloesungFotos,
            //     targetHeight: MyApp.current.aufloesungFotos,
            //     correctOrientation: true,
            // });

            // Beispiel: pictureSrc='file:///storage/emulated/0/Android/data/de.recomobil.app/cache/1570482608018.jpg'

            if (pictureSrc && pictureSrc.startsWith('file:')) {
                // Extract just the filename. Result example: cdv_photo_003.jpg
                const tempFilename = FileHelper.getFilename(pictureSrc);

                // Now, the opposite. Extract the full path, minus filename.
                // Result example: file:///var/mobile/Containers/Data/Application
                // /E4A79B4A-E5CB-4E0C-A7D9-0603ECD48690/tmp/
                const tempBaseFilesystemPath = FileHelper.getDirectory(pictureSrc);

                // Get the Data directory on the device.
                // Result example: file:///var/mobile/Containers/Data/Application
                // /E4A79B4A-E5CB-4E0C-A7D9-0603ECD48690/Library/NoCloud/
                const newBaseFilesystemPath = this.file.dataDirectory;
                const newFilename = FileHelper.removeIllegalFilenameChars(options.filenamePrefix + '-' + tempFilename);

                const entry = await this.file.copyFile(tempBaseFilesystemPath, tempFilename, newBaseFilesystemPath, newFilename);

                log?.debug('copyFile', entry);

                // Result example: file:///var/mobile/Containers/Data/Application
                // /E4A79B4A-E5CB-4E0C-A7D9-0603ECD48690/Library/NoCloud/cdv_photo_003.jpg
                pictureSrc = newBaseFilesystemPath + newFilename;

                let text = options.text;

                if (text == null) {
                    text = AppConfig.current.einstellungen.FotosTextSonstige;
                }

                if (text) {
                    const now = moment();

                    let platzhalter: any = {
                        'AktuellesDatum': now.format('DD.MM.YYYY'),
                        'AktuelleUhrzeit': now.format('HH:mm'),
                        'Fahrzeug': MyApp.current.getFahrzeugKennzeichen(),
                        'PersonalKey': MyApp.current.getPersonalKey(),
                        'Personalnummer': MyApp.current.getPersonalnummer(),
                        'PersonalName': MyApp.current.getPersonalName(),
                    }

                    if (text.includes('{GPS}')) {
                        platzhalter['GPS'] = await SystemService.instance.getAktuelleGpsPositionStr('Foto', 10);
                    }

                    if (options.textPlatzhalter) {
                        platzhalter = {
                            ...platzhalter,
                            ...options.textPlatzhalter
                        };
                    }

                    const base64DataUrl = await this.file.readAsDataURL(newBaseFilesystemPath, newFilename);

                    const blob = await ImageHelper.writeImageText(base64DataUrl, text, platzhalter);

                    await this.file.writeExistingFile(newBaseFilesystemPath, newFilename, blob);
                }

                // await this.camera.cleanup();
            }

        } catch (err) {
            const message = Utils.getErrorMessage(err);

            log.warn('camera error: ' + message, err);

            const code = err?.code;

            if (code === "UNIMPLEMENTED") {
                err = 'Kamera im Browser nicht verfügbar.';

                // Derzeit nur zum testen
                pictureSrc = 'R0lGODlhPQBEAPeoAJosM//AwO/AwHVYZ/z595kzAP/s7P+goOXMv8+fhw/v739/f+8PD98fH/8mJl+fn/9ZWb8/PzWlwv///6wWGbImAPgTEMImIN9gUFCEm/gDALUL'
                    + 'DN8PAD6atYdCTX9gUNKlj8wZAKUsAOzZz+UMAOsJAP/Z2ccMDA8PD/95eX5NWvsJCOVNQPtfX/8zM8+QePLl38MGBr8JCP+zs9myn/8GBqwpAP/GxgwJCPny78lzYLgjAJ8vAP9'
                    + 'fX/+MjMUcAN8zM/9wcM8ZGcATEL+QePdZWf/29uc/P9cmJu9MTDImIN+/r7+/vz8/P8VNQGNugV8AAF9fX8swMNgTAFlDOICAgPNSUnNWSMQ5MBAQEJE3QPIGAM9AQMqGcG9vb6'
                    + 'MhJsEdGM8vLx8fH98AANIWAMuQeL8fABkTEPPQ0OM5OSYdGFl5jo+Pj/+pqcsTE78wMFNGQLYmID4dGPvd3UBAQJmTkP+8vH9QUK+vr8ZWSHpzcJMmILdwcLOGcHRQUHxwcK9PT'
                    + '9DQ0O/v70w5MLypoG8wKOuwsP/g4P/Q0IcwKEswKMl8aJ9fX2xjdOtGRs/Pz+Dg4GImIP8gIH0sKEAwKKmTiKZ8aB/f39Wsl+LFt8dgUE9PT5x5aHBwcP+AgP+WltdgYMyZfyyw'
                    + 'z78AAAAAAAD///8AAP9mZv///wAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA'
                    + 'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA'
                    + 'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAACH5BAEAAKgALAAAAAA9AEQAAAj/AFEJH'
                    + 'EiwoMGDCBMqXMiwocAbBww4nEhxoYkUpzJGrMixogkfGUNqlNixJEIDB0SqHGmyJSojM1bKZOmyop0gM3Oe2liTISKMOoPy7GnwY9CjIYcSRYm0aVKSLmE6nfq05QycVLPuhDrx'
                    + 'BlCtYJUqNAq2bNWEBj6ZXRuyxZyDRtqwnXvkhACDV+euTeJm1Ki7A73qNWtFiF+/gA95Gly2CJLDhwEHMOUAAuOpLYDEgBxZ4GRTlC1fDnpkM+fOqD6DDj1aZpITp0dtGCDhr+f'
                    + 'VuCu3zlg49ijaokTZTo27uG7Gjn2P+hI8+PDPERoUB318bWbfAJ5sUNFcuGRTYUqV/3ogfXp1rWlMc6awJjiAAd2fm4ogXjz56aypOoIde4OE5u/F9x199dlXnnGiHZWEYbGpsA'
                    + 'EA3QXYnHwEFliKAgswgJ8LPeiUXGwedCAKABACCN+EA1pYIIYaFlcDhytd51sGAJbo3onOpajiihlO92KHGaUXGwWjUBChjSPiWJuOO/LYIm4v1tXfE6J4gCSJEZ7YgRYUNrkji'
                    + '9P55sF/ogxw5ZkSqIDaZBV6aSGYq/lGZplndkckZ98xoICbTcIJGQAZcNmdmUc210hs35nCyJ58fgmIKX5RQGOZowxaZwYA+JaoKQwswGijBV4C6SiTUmpphMspJx9unX4Kaimj'
                    + 'Dv9aaXOEBteBqmuuxgEHoLX6Kqx+yXqqBANsgCtit4FWQAEkrNbpq7HSOmtwag5w57GrmlJBASEU18ADjUYb3ADTinIttsgSB1oJFfA63bduimuqKB1keqwUhoCSK374wbujvOS'
                    + 'u4QG6UvxBRydcpKsav++Ca6G8A6Pr1x2kVMyHwsVxUALDq/krnrhPSOzXG1lUTIoffqGR7Goi2MAxbv6O2kEG56I7CSlRsEFKFVyovDJoIRTg7sugNRDGqCJzJgcKE0ywc0ELm6'
                    + 'KBCCJo8DIPFeCWNGcyqNFE06ToAfV0HBRgxsvLThHn1oddQMrXj5DyAQgjEHSAJMWZwS3HPxT/QMbabI/iBCliMLEJKX2EEkomBAUCxRi42VDADxyTYDVogV+wSChqmKxEKCDAY'
                    + 'FDFj4OmwbY7bDGdBhtrnTQYOigeChUmc1K3QTnAUfEgGFgAWt88hKA6aCRIXhxnQ1yg3BCayK44EWdkUQcBByEQChFXfCB776aQsG0BIlQgQgE8qO26X1h8cEUep8ngRBnOy74E'
                    + '9QgRgEAC8SvOfQkh7FDBDmS43PmGoIiKUUEGkMEC/PJHgxw0xH74yx/3XnaYRJgMB8obxQW6kL9QYEJ0FIFgByfIL7/IQAlvQwEpnAC7DtLNJCKUoO/w45c44GwCXiAFB/OXAAT'
                    + 'QryUxdN4LfFiwgjCNYg+kYMIEFkCKDs6PKAIJouyGWMS1FSKJOMRB/BoIxYJIUXFUxNwoIkEKPAgCBZSQHQ1A2EWDfDEUVLyADj5AChSIQW6gu10bE/JG2VnCZGfo4R4d0sdQoB'
                    + 'AHhPjhIB94v/wRoRKQWGRHgrhGSQJxCS+0pCZbEhAAOw==';
            } else if (typeof (message) === 'string' && message.startsWith('User cancelled')) {
                // Aufnahme wurde abgebrochen
            } else if (err == 20) {
                await UiHelper.showAlert('ReCoMobil hat kein Zugriff auf die Kamera. Bitte Berechtigung für die Kamera in den App-Einstellungen aktivieren', 'Fehler', true);
            } else if (err == 'No Image Selected') {
                // Aufnahme wurde abgebrochen
            } else {
                UiHelper.showError('Fehler Kamera: ' + Utils.getErrorMessage(err), 'Fehler', false, false);
            }
        }

        setTimeout(() => {
            CameraHelper.isAktiv = false;
        }, 250);

        return pictureSrc;
    }
}